import React, { useEffect, useState, useCallback } from 'react';
import { Search, X, User as UserIcon } from 'lucide-react';
import { adminApi } from '../../lib/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString();
};

const Cell = ({ children }) => <span>{children || <span className="text-[#b0a496]">—</span>}</span>;

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async (term) => {
    setLoading(true);
    try {
      setUsers(await adminApi.listUsers(term));
    } catch (_) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openUser = async (u) => {
    setSelected(u);
    setDetail(null);
    try {
      setDetail(await adminApi.getUser(u.id));
    } catch (_) {
      setDetail(u);
    }
  };

  return (
    <div className="mt-8" data-testid="admin-users-tab">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="relative w-full max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6a5a]" strokeWidth={1.6} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone…"
            data-testid="user-search-input"
            className="w-full bg-white border border-[#cdc2b1] pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#1a1410]"
          />
        </div>
        <div className="text-[11px] tracking-[0.22em] text-[#7a6a5a]">{users.length} USER{users.length !== 1 ? 'S' : ''}</div>
      </div>

      <div className="overflow-x-auto border border-[#e5dfd0]">
        <table className="w-full text-sm">
          <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
            <tr>
              <th className="p-4">NAME</th>
              <th className="p-4">PHONE</th>
              <th className="p-4">ADDRESS</th>
              <th className="p-4">CITY</th>
              <th className="p-4">POSTAL / PIN</th>
              <th className="p-4">COUNTRY</th>
              <th className="p-4">REGISTERED</th>
              <th className="p-4">LAST LOGIN</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-[#7a6a5a]">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-[#7a6a5a]">No users found.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-[#e5dfd0] align-top hover:bg-[#efe8d8]/60" data-testid={`user-row-${u.id}`}>
                <td className="p-4">
                  <div className="font-serif text-base">{u.name || <span className="text-[#b0a496]">Unnamed</span>}</div>
                  {u.is_admin && <span className="text-[10px] tracking-[0.18em] text-[#7a5a1a]">ADMIN</span>}
                </td>
                <td className="p-4 font-mono text-xs"><Cell>{u.phone}</Cell></td>
                <td className="p-4 text-xs max-w-[180px]"><Cell>{u.address}</Cell></td>
                <td className="p-4 text-xs"><Cell>{u.city}</Cell></td>
                <td className="p-4 text-xs"><Cell>{u.postal_code}</Cell></td>
                <td className="p-4 text-xs"><Cell>{u.country}</Cell></td>
                <td className="p-4 text-xs text-[#5a4a3e]">{fmtDate(u.created_at)}</td>
                <td className="p-4 text-xs text-[#5a4a3e]">{fmtDate(u.last_login)}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => openUser(u)} data-testid={`view-user-${u.id}`} className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] hover:text-black">
                    <UserIcon className="w-3.5 h-3.5" strokeWidth={1.4} /> VIEW
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#f5f1e6] border border-[#cdc2b1] max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">USER PROFILE</div>
                <h3 className="font-serif text-3xl mt-3">{selected.name || 'Unnamed'}</h3>
              </div>
              <button onClick={() => setSelected(null)} data-testid="close-user-detail" className="text-[#7a6a5a] hover:text-black"><X className="w-5 h-5" /></button>
            </div>

            {!detail ? (
              <div className="mt-8 text-[#7a6a5a]">Loading…</div>
            ) : (
              <div className="mt-6 space-y-5 text-sm">
                <Row label="Phone (verified)" value={detail.phone} mono />
                <Row label="Email" value={detail.email} />
                <Row label="Sign-in method" value={(detail.auth_provider || '').toUpperCase()} />
                <Row label="Registered" value={fmtDate(detail.created_at)} />
                <Row label="Last login" value={fmtDate(detail.last_login)} />
                <Row label="Orders placed" value={detail.orders_count != null ? String(detail.orders_count) : '—'} />

                <div className="pt-2">
                  <div className="text-[10px] tracking-[0.22em] text-[#7a6a5a] mb-2">ADDRESSES</div>
                  {(detail.addresses && detail.addresses.length > 0) ? (
                    <div className="space-y-3">
                      {detail.addresses.map((a) => (
                        <div key={a.id} className="border border-[#cdc2b1] p-4">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] tracking-[0.18em]">{(a.label || 'Address').toUpperCase()}</span>
                            {a.is_default && <span className="text-[10px] px-2 py-0.5 bg-[#e6efe1] border border-[#b7ceac] text-[#3d5a2a]">DEFAULT</span>}
                          </div>
                          <div className="mt-2 text-[#3a2e26]">{a.full_name}</div>
                          <div className="text-[#5a4a3e]">{a.address}</div>
                          <div className="text-[#5a4a3e]">{[a.city, a.postal_code].filter(Boolean).join(', ')}</div>
                          <div className="text-[#5a4a3e]">{a.country}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[#b0a496]">No saved addresses.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-6 border-b border-[#e5dfd0] pb-3">
    <span className="text-[10px] tracking-[0.22em] text-[#7a6a5a] uppercase">{label}</span>
    <span className={`text-right text-[#1a1410] ${mono ? 'font-mono text-xs' : ''}`}>{value || <span className="text-[#b0a496]">—</span>}</span>
  </div>
);

export default UsersTab;
